import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import { PageHeader, AppButton, FileUpload, FilePreview } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import "./general-assignments.scss";

const GeneralAssignmentForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [batchId, setBatchId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [maxMarks, setMaxMarks] = useState("");
  const [files, setFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [removeAttachments, setRemoveAttachments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    apiClient.get("/batches/list")
      .then(({ data }) => setBatches(data.batches || data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    apiClient.get(`/general-assignments/detail/${id}`)
      .then(({ data }) => {
        const a = data.assignment;
        setTitle(a.title);
        setDescription(a.description || "");
        setBatchId(a.batchId?._id || a.batchId || "");
        setDueDate(a.dueDate ? new Date(a.dueDate).toISOString().slice(0, 16) : "");
        setMaxMarks(String(a.maxMarks));
        setExistingAttachments(a.attachments || []);
      })
      .catch(() => setToast({ show: true, message: "Failed to load assignment", type: "error" }))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleRemoveExisting = (fileKey) => {
    setExistingAttachments((prev) => prev.filter((a) => a.fileKey !== fileKey));
    setRemoveAttachments((prev) => [...prev, fileKey]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !batchId || !dueDate || !maxMarks) {
      setToast({ show: true, message: "Please fill all required fields", type: "error" });
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("description", description.trim());
      formData.append("batchId", batchId);
      formData.append("dueDate", dueDate);
      formData.append("maxMarks", maxMarks);

      if (isEdit && removeAttachments.length) {
        formData.append("removeAttachments", JSON.stringify(removeAttachments));
      }

      files.forEach((f) => formData.append("attachments", f));

      if (isEdit) {
        await apiClient.put(`/general-assignments/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setToast({ show: true, message: "Assignment updated", type: "success" });
      } else {
        await apiClient.post("/general-assignments/add", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setToast({ show: true, message: "Assignment created", type: "success" });
      }

      const prefix = getCurrentUser()?.usertype === "instructor" ? "/i/batch-assignments" : "/batch-assignments";
      setTimeout(() => navigate(prefix), 600);
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || "Failed to save assignment",
        type: "error",
      });
    }
    setSaving(false);
  };

  if (loading) return <div className="ga-page ga-loading">Loading...</div>;

  return (
    <div className="ga-page">
      <PageHeader
        backButton
        icon={ClipboardList}
        title={isEdit ? "Edit Assignment" : "Create Assignment"}
        description={isEdit ? "Update assignment details" : "Create a new text/file-based assignment"}
      />

      <form className="ga-form" onSubmit={handleSubmit}>
        <div className="ga-form__group">
          <label className="ga-form__label">Title *</label>
          <input
            className="ga-form__input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Assignment title"
            required
          />
        </div>

        <div className="ga-form__group">
          <label className="ga-form__label">Batch *</label>
          <select
            className="ga-form__select"
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            required
          >
            <option value="">Select batch</option>
            {batches.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
        </div>

        <div className="ga-form__row">
          <div className="ga-form__group">
            <label className="ga-form__label">Due Date *</label>
            <input
              className="ga-form__input"
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
            />
          </div>
          <div className="ga-form__group">
            <label className="ga-form__label">Max Marks *</label>
            <input
              className="ga-form__input"
              type="number"
              min="0"
              value={maxMarks}
              onChange={(e) => setMaxMarks(e.target.value)}
              placeholder="e.g. 100"
              required
            />
          </div>
        </div>

        <div className="ga-form__group">
          <label className="ga-form__label">Description</label>
          <textarea
            className="ga-form__textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Assignment description, instructions, or paste a GitHub link..."
            rows={5}
          />
        </div>

        {existingAttachments.length > 0 && (
          <div className="ga-form__group">
            <label className="ga-form__label">Current Attachments</label>
            <FilePreview
              attachments={existingAttachments}
              onRemove={handleRemoveExisting}
            />
          </div>
        )}

        <div className="ga-form__group">
          <FileUpload
            label={isEdit ? "Add More Attachments" : "Attachments (optional)"}
            files={files}
            onChange={setFiles}
            maxFiles={5}
          />
        </div>

        <div className="ga-form__actions">
          <AppButton type="submit" disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Update Assignment" : "Create Assignment"}
          </AppButton>
          <AppButton
            type="button"
            variant="outline"
            onClick={() => navigate(getCurrentUser()?.usertype === "instructor" ? "/i/batch-assignments" : "/batch-assignments")}
            disabled={saving}
          >
            Cancel
          </AppButton>
        </div>
      </form>

      <CommonToaster toast={toast} setToast={setToast} />
    </div>
  );
};

export default GeneralAssignmentForm;
